import React, { useEffect, useState } from "react";
import { Button, Col, Container, Row } from "reactstrap";
import Stepper from "../components/Stepper";
import { useDispatch, useSelector } from "react-redux";
import PricingSection from "../components/PricingSection";
import OrderedItems from "../components/OrderedItems";
import GeneralInfoSection from "../components/GeneralInfoSection";
import MainNavigation from "../components/MainNavigation";
import { useHistory, useParams } from "react-router";
import {
  getDriverDetail,
  trackOrder,
} from "../store/actions/restaurantsAction";
import moment from "moment";
import OrderCompleted from "../components/OrderCompleted";
import DriverDetailModal from "../components/DriverDetailModal";
import RestaurantLoader from "../utils/RestaurantLoader";

const Tracking = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { darkMode, color } = useSelector((state) => state.global);
  const { orderTracker, driverDetail } = useSelector(
    (state) => state.restaurants
  );

  const [driverModal, setDriverModal] = useState(false);
  const toggleDriverModal = () => setDriverModal(!driverModal);
  const { id } = useParams();

  const seconds = orderTracker?.createdAt?.seconds;
  const nano = orderTracker?.createdAt?.nanoseconds;
  const [localLoading, setLocalLoading] = useState(true);
  const unixTimestamp = seconds * 1000 + nano / 1000000;
  const date = moment.unix(unixTimestamp / 1000);
  const formattedDateTime = date.format("DD MMM YYYY, HH:mm A");
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);

  const getMaxEstimatedTime = (orders = []) => {
    const times = orders
      ?.map((item) => {
        const time = item?.estimatedTime;
        if (!time) return null;
        const match = time.match(/\d+/g);
        return match ? Math.max(...match?.map(Number)) : null;
      })
      .filter((val) => val !== null);

    if (!times?.length) return null;

    const maxTime = Math.max(...times);
    const matchingRange = orders.find((item) =>
      item.estimatedTime?.includes(String(maxTime))
    )?.estimatedTime;

    return matchingRange || `${maxTime} min`;
  };

  const steps = [
    {
      rightContent: "Order Placed",
      desc: `We received your order on ${formattedDateTime}`,
      isComplete: [
        "pending",
        "processing",
        "ready",
        "refunded",
        "completed",
      ].includes(orderTracker.status),
    },
    {
      rightContent: "In Progress",
      desc: `We're preparing your delicious food${
        getMaxEstimatedTime(orderTracker?.order)?.length
          ? ` – it should be ready in about ${getMaxEstimatedTime(
              orderTracker?.order
            )} minutes.`
          : "."
      }`,
      isComplete: ["processing", "ready", "refunded", "completed"].includes(
        orderTracker.status
      ),
    },
    {
      rightContent:
        orderTracker?.orderType === "delivery"
          ? "Out for Delivery"
          : orderTracker?.orderType === "dinein"
          ? "Ready to Serve"
          : "Ready for TakeAway",
      desc:
        orderTracker?.orderType === "delivery" ? (
          orderTracker?.driverId ? (
            <>
              Your order is on its way to your location and assigned to a
              driver.{" "}
              <Button
                color="link"
                onClick={toggleDriverModal}
                style={{
                  padding: 0,
                  fontSize: "inherit",
                  color,
                  verticalAlign: "baseline",
                  textDecoration: "underline",
                }}
                className="fw--400 align-baseline m-0 p-0"
              >
                Driver Detail
              </Button>
            </>
          ) : (
            "Your order is on its way to your location."
          )
        ) : orderTracker?.orderType === "dinein" ? (
          "Your order is ready and will be served at your table shortly."
        ) : (
          "Your order is ready for pickup at the counter."
        ),
      isComplete: ["ready", "refunded", "completed"].includes(
        orderTracker.status
      ),
    },
    {
      rightContent:
        orderTracker?.orderType === "delivery"
          ? "Delivered"
          : orderTracker?.orderType === "dinein"
          ? "Served"
          : "Picked Up",
      desc:
        orderTracker?.orderType === "delivery"
          ? "Your order has been delivered successfully. Enjoy your meal!"
          : orderTracker?.orderType === "dinein"
          ? "Your order has been served at your table. Enjoy your meal!"
          : "You have picked up your order. Enjoy your meal!",
      isComplete: ["refunded", "completed"].includes(orderTracker.status),
    },
    {
      rightContent: "Refunded",
      desc: "Your payment has been refunded successfully.",
      isComplete: orderTracker.status === "refunded",
    },
  ];

  useEffect(() => {
    dispatch(trackOrder(id));
    window.scrollTo(0, 0);
  }, [dispatch, id]);

  useEffect(() => {
    if (orderTracker?.driverId) {
      dispatch(getDriverDetail(orderTracker?.driverId));
    }
  }, [dispatch, orderTracker]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLocalLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  if (localLoading) {
    return (
      <div className="cart">
        <Container className="py-5 d-flex justify-content-center align-items-center">
          <RestaurantLoader message="Fetching Order Tracking Details" />
        </Container>
      </div>
    );
  }

  return (
    <div className="cart">
      <Container>
        <MainNavigation title={"Tracking"} />
        <Row className="d-flex my-3 flex-column align-items-center justify-content-center">
          <Col className="d-flex justify-content-center"></Col>
        </Row>
        <Row className="d-flex my-3 flex-column align-items-center justify-content-center">
          <Col className="d-flex justify-content-start align-items-center pl-5">
            <Stepper
              steps={steps}
              completeColor={`${color}`}
              darkMode={darkMode}
              tracker={orderTracker}
            />
          </Col>
        </Row>
        <Row className="d-flex my-3 flex-column align-items-center justify-content-center">
          <Col className="d-flex justify-content-end align-items-end px-3">
            {orderTracker?.tableNumber && (
              <>
                <Button
                  className="cart__contact_btn text-capitalize"
                  style={{ background: `${color}` }}
                  onClick={() =>
                    history.push({
                      pathname: `/table/${orderTracker?.id}/${orderTracker?.tableNumber}`,
                      state: orderTracker,
                    })
                  }
                >
                  Contact support
                </Button>
              </>
            )}
          </Col>
        </Row>
        <GeneralInfoSection tracker={orderTracker} />

        <OrderedItems
          data={orderTracker?.order || orderTracker?.cartItems}
          title={"Order Info"}
        />
        <OrderedItems
          data={orderTracker?.paymentMethod}
          title={"Payment Info"}
        />

        <PricingSection
          data={{
            price: orderTracker?.totalPriceItem,
            total: orderTracker?.totalAmount,
            tip: orderTracker?.tip,
            currency: orderTracker?.currency,
            appliedCharges: orderTracker?.appliedCharges,
            finalPrice: orderTracker?.finalPrice,
            basePrice: orderTracker?.totalAmount,
          }}
        />
      </Container>
      <OrderCompleted toggle={toggle} modal={modal} />
      <DriverDetailModal
        isOpen={driverModal}
        toggle={toggleDriverModal}
        driver={driverDetail}
        darkMode={darkMode}
        color={color}
      />
    </div>
  );
};

export default Tracking;
